import { useQuery } from "@tanstack/react-query";
import { ShipTier } from "@voidex/contracts-ts";
import { querySubgraph } from "../web3/subgraph";

export interface OwnedShip {
	tokenId: bigint;
	tier: ShipTier;
}

interface SubgraphShip {
	id: string;
	tier: "Probe" | "Frigate" | "Dreadnought";
}

interface ShipQueryResult {
	ships: SubgraphShip[];
}

const tierMap: Record<string, ShipTier> = {
	Probe: ShipTier.Probe,
	Frigate: ShipTier.Frigate,
	Dreadnought: ShipTier.Dreadnought,
};

/**
 * Fetches all ships owned by `address` from the subgraph.
 * Scales to any number of ships without RPC block-range limits.
 */
export function useShipInventory(address?: `0x${string}`) {
	return useQuery({
		queryKey: ["shipInventory", address],
		queryFn: async (): Promise<OwnedShip[]> => {
			if (!address) return [];

			const ownerId = address.toLowerCase();
			const data = await querySubgraph<ShipQueryResult>(
				`query Ships($owner: String!) {
					ships(
						where: { owner: $owner, burned: false }
						orderBy: id
						orderDirection: asc
						first: 1000
					) {
						id
						tier
					}
				}`,
				{ owner: ownerId },
			);

			return data.ships.map((s) => ({
				tokenId: BigInt(s.id),
				tier: tierMap[s.tier] ?? ShipTier.Probe,
			}));
		},
		enabled: !!address,
		staleTime: 10_000,
		refetchInterval: 15_000,
	});
}

export function useShipsByTier(address?: `0x${string}`) {
	const { data: ships, ...rest } = useShipInventory(address);

	const all = ships ?? [];
	const probes = all.filter((s) => s.tier === ShipTier.Probe);
	const frigates = all.filter((s) => s.tier === ShipTier.Frigate);
	const dreadnoughts = all.filter((s) => s.tier === ShipTier.Dreadnought);

	return { ships: all, probes, frigates, dreadnoughts, ...rest };
}
